// 양식 전역 변수(template.variables) 참조 조망 — "이 변수를 누가 쓰나"의 순수 수집 층.
//
// 목적(결정 2026-08-10-composer-system-query-terminology §4): 변수 리네임/삭제가 지정을 부순
// **뒤에야** 경고가 뜨는 현재 동작을, 편집 전에 파급이 보이는 사전 인지로 바꾼다.
//
// ⚠️ 카탈로그 축(collectCatalogConsumers)과 **판정 방식이 다르다**. `catalogKey`는 이름이
// 독특한 *속성명*이라 "그 이름의 속성을 만나면 참조"로 전수 순회가 성립하지만, 변수 key는
// top-level content 키 = 여러 속성에 담기는 **값**이다. 값 동일성만으로 판정하면 라벨·옵션
// 값·데이터셋 필드명이 우연히 같을 때 전부 참조로 잡힌다. 그래서 여기선
//  ① 참조를 담는 **속성명**을 열거해 형태를 가르고(REFERENCE_HOLDERS),
//  ② 열거 밖 속성은 값이 변수 key와 **정확히 같을 때만** `other`로 낸다(분류 불가 ≠ 미표시).
// 선언·내용 성격이 명백한 속성명(key/label/value 등)은 ②에서 제외한다 — 그것까지 참조로 세면
// 조망이 소음이 되어 "×N"의 신뢰가 무너진다.

import type { ReportTemplate } from '@safetyhub/shared';

import { parseCondition } from './conditionExpr';

/**
 * 변수 한 건을 참조하는 소비처 한 형태 — 변수 편집기의 "참조하는 곳 ×N" 한 줄.
 *
 * `kind`는 참조 형태의 축(테스트·후속 분기용), `label`은 저작자가 읽는 문장, `count`는 같은
 * 형태의 참조가 여러 자리일 때의 합산(셀 10개가 같은 변수를 조건으로 쓰면 한 줄 ×10).
 */
export interface VariableConsumerRef {
  kind:
    | 'catalog-param'
    | 'binding'
    | 'switch'
    | 'visible-when'
    | 'enabled-when'
    | 'branch-condition'
    | 'label-suffix'
    | 'write-target'
    | 'modal-commit'
    | 'repeat-over'
    | 'options-source'
    | 'other';
  label: string;
  count: number;
}

/** 소비처 라벨의 영역 접두 — 참조가 템플릿의 어느 층에 있는지(최상위 필드명 기준). */
const CONSUMER_AREA_LABEL: Record<string, string> = {
  body: '본문',
  composerWidgetTree: '작성 폼',
  dataDefs: '데이터셋',
  dataSources: '데이터 소스',
  composeChrome: '작성 화면',
  draftMeta: '업로드 초안(미적용)',
};

/**
 * 순회에서 통째로 건너뛰는 최상위 필드.
 *
 *  - `variables` — 선언 자신이다(소비처가 아니다). 변수가 다른 변수를 참조하는 축은 없다.
 *  - `catalogParams` — 지정은 형상이 달라(`{from:'variable', key}`) 앞서 명시 처리한다.
 *  - `detailChrome` — 여기의 `visibleWhen`은 **chrome-scope**(DETAIL_CHROME_CONDITION_VARS)를
 *    읽는 별개 축이라, 같은 이름의 전역 변수가 있어도 참조가 아니다(오탐 차단).
 */
const SKIPPED_TOP_LEVEL = new Set(['variables', 'catalogParams', 'detailChrome']);

/**
 * 값이 **조건식**(evaluateCondition 문법)인 속성 — 식 안의 식별자를 뽑아 대조한다.
 *
 * 분기 슬롯 `condition` 하나뿐이다. `visibleWhen`/`enabledWhen`은 이름만 조건이지 값은 **단일
 * 키**이고 평가도 `values[w.visibleWhen]` 직접 조회다(rc composerWidgetTreeRender +
 * composerWidgetTree의 프리셋 재매핑이 둘을 bindingKey 축으로 다룬다) — 그래서 아래
 * REFERENCE_HOLDERS 소속이다. 여기에 두면 kebab key(`risk-method`)가 조건 파서의
 * `CONDITION_IDENT_RE`(하이픈 불허)에 걸려 조용히 누락된다.
 */
const CONDITION_HOLDERS: Record<string, { kind: VariableConsumerRef['kind']; what: string }> = {
  condition: { kind: 'branch-condition', what: '분기 슬롯 조건' },
};

/**
 * 값이 참조 키(그 자체 또는 하위 경로)인 속성 — 속성명으로 참조 형태를 가른다.
 *
 * bindingKey 축(작성 스코프 values 참조)은 rc `composerWidgetTree.ts`의 "참조 축 전수"
 * 주석과 짝을 맞춘다: bindingKey · visibleWhen · enabledWhen · labelSuffixFrom ·
 * writes[].target · commit[].to(+ purpose.commit[].to) · 계산 셀 compute 입력의 bindingKey.
 * `over`/`sourceKey`/`optionsSource(s)`는 본래 데이터셋·카탈로그 키 축이지만 전역 변수와
 * **같은 top-level 이름 공간**을 쓰므로, 겹치면 그 자체가 저작자가 알아야 할 충돌이라 남긴다.
 */
const REFERENCE_HOLDERS: Record<string, { kind: VariableConsumerRef['kind']; what: string }> = {
  bindingKey: { kind: 'binding', what: '바인딩' },
  visibleWhen: { kind: 'visible-when', what: '표시 조건' },
  enabledWhen: { kind: 'enabled-when', what: '활성 조건' },
  labelSuffixFrom: { kind: 'label-suffix', what: '라벨 병기' },
  target: { kind: 'write-target', what: '값 전파 대상(writes)' },
  to: { kind: 'modal-commit', what: '모달 확인 기록 대상' },
  over: { kind: 'repeat-over', what: '반복 소스' },
  sourceKey: { kind: 'repeat-over', what: '반복 행 소스' },
  optionsSource: { kind: 'options-source', what: '옵션 소스' },
};

/**
 * 열거 밖 속성의 값 동일성 판정에서 제외할 속성명 — **선언·내용** 성격이라 참조가 아니다.
 * (데이터셋 필드 `key`가 변수와 같은 이름일 수 있고, `label`/`value`/`text`는 사람이 읽는 내용이다.)
 */
const NON_REFERENCE_PROPS = new Set([
  'key',
  'id',
  'name',
  'label',
  'title',
  'value',
  'text',
  'path',
  'field',
  'group',
  'kind',
  'widget',
  'placeholder',
]);

/** 참조 키가 이 변수를 가리키는가 — 그 자체이거나 하위 경로(`key.` / `key[].`) 소비. */
function refersTo(raw: string, variableKey: string): boolean {
  return (
    raw === variableKey ||
    raw.startsWith(`${variableKey}.`) ||
    raw.startsWith(`${variableKey}[].`)
  );
}

/**
 * 조건식이 참조하는 식별자 목록.
 *
 * 화이트리스트 문법을 벗어난 식은 `parseCondition`이 null이라, 그 경우 식별자 스캔으로
 * 폴백한다(파싱 실패를 참조 0으로 두면 그게 미표시다).
 */
function conditionIdents(expr: string): string[] {
  const model = parseCondition(expr);
  if (model) return model.clauses.map((c) => c.key).filter((k) => k !== '');
  return expr.match(/[A-Za-z_$][A-Za-z0-9_$.]*/g) ?? [];
}

/**
 * 이 템플릿에서 전역 변수를 **참조하는 모든 자리**의 열거 — 변수 편집기 조망의 원료.
 *
 * 돌려주는 Map의 키는 `TemplateVariable.key`이고, 선언된 변수는 참조가 없어도 **빈 배열로 반드시
 * 들어간다**(호출부가 "참조 없음"을 없는 키와 구분할 수 있어야 한다).
 */
export function collectVariableConsumers(
  template: ReportTemplate,
  /**
   * 시스템 조회 key → 사람이 읽는 이름. 라벨에 raw key 대신 이름을 싣기 위한 선택 인자다
   * (미지정·미상 key는 key 그대로 — 조망이 조용히 비는 것보다 낫다).
   */
  catalogLabelOf?: (catalogKey: string) => string | undefined,
): Map<string, VariableConsumerRef[]> {
  const variableKeys = (template.variables ?? []).map((v) => v.key).filter((k) => k !== '');
  // variableKey → (kind|label → ref) — 같은 형태는 count 합산.
  const acc = new Map<string, Map<string, VariableConsumerRef>>();
  for (const k of variableKeys) acc.set(k, new Map());

  const add = (variableKey: string, kind: VariableConsumerRef['kind'], label: string): void => {
    const byLabel = acc.get(variableKey);
    if (!byLabel) return; // 선언되지 않은 키 참조는 이 조망의 대상이 아니다(유령 지정 경고는 별도 축).
    const id = `${kind}|${label}`;
    const existing = byLabel.get(id);
    if (existing) existing.count += 1;
    else byLabel.set(id, { kind, label, count: 1 });
  };

  /** 참조 문자열 한 개를 모든 변수와 대조 — 여러 변수가 같은 접두를 가질 수 있어 전수로 돈다. */
  const matchRef = (raw: string, kind: VariableConsumerRef['kind'], label: string): void => {
    for (const vk of variableKeys) if (refersTo(raw, vk)) add(vk, kind, label);
  };

  // ── 1. 조회 파라미터 지정(catalogParams) — 형상이 달라 순회로 안 잡힌다. ──
  // 이 조망의 핵심 동기(결정 §4)이자, "어느 시스템 조회가 이 변수를 먹나"가 파급 판단의 알맹이라
  // 카탈로그 key를 라벨에 싣는다(지정은 카탈로그당 소수라 줄이 폭발하지 않는다).
  for (const [catalogKey, params] of Object.entries(template.catalogParams ?? {})) {
    for (const binding of Object.values(params ?? {})) {
      if (binding?.from !== 'variable') continue;
      const shown = catalogLabelOf?.(catalogKey) || catalogKey;
      add(binding.key, 'catalog-param', `시스템 조회 '${shown}' 조회 파라미터 지정`);
    }
  }

  // ── 2. 구조 전수 순회 ──
  const seen = new Set<object>();
  const walk = (value: unknown, path: string[]): void => {
    if (value === null || typeof value !== 'object') return;
    if (seen.has(value as object)) return;
    seen.add(value as object);
    if (Array.isArray(value)) {
      value.forEach((item, i) => walk(item, [...path, String(i)]));
      return;
    }
    const obj = value as Record<string, unknown>;
    const area = CONSUMER_AREA_LABEL[path[0]] ?? path[0];

    for (const [prop, child] of Object.entries(obj)) {
      if (typeof child === 'string' && child !== '') {
        const cond = CONDITION_HOLDERS[prop];
        if (cond) {
          for (const ident of conditionIdents(child)) {
            matchRef(ident, cond.kind, `${area} ${cond.what}`);
          }
          continue;
        }
        // 표시 형식 분기(BindingFormat switch)의 `on` — `kind:'switch'`를 가진 객체에서만
        // 참조로 본다(`on`은 흔한 이름이라 형상 확인 없이 잡으면 오탐이 난다).
        if (prop === 'on') {
          if (obj.kind === 'switch') matchRef(child, 'switch', `${area} 표시 형식 분기(switch)`);
          continue;
        }
        const holder = REFERENCE_HOLDERS[prop];
        if (holder) {
          matchRef(child, holder.kind, `${area} ${holder.what}`);
          continue;
        }
        // 열거 밖 — 값이 변수 key와 **정확히** 같을 때만 낸다(하위 경로 접두는 오탐이 커 제외).
        if (!NON_REFERENCE_PROPS.has(prop) && acc.has(child)) {
          add(child, 'other', `${area} 기타 참조(${prop})`);
        }
        continue;
      }
      // 옵션 소스 복수형 — 문자열 배열이라 위 스칼라 분기에 걸리지 않는다.
      if (prop === 'optionsSources' && Array.isArray(child)) {
        for (const s of child) {
          if (typeof s === 'string' && s !== '') matchRef(s, 'options-source', `${area} 옵션 소스`);
        }
        continue;
      }
      walk(child, [...path, prop]);
    }
  };
  for (const [k, v] of Object.entries(template)) {
    if (SKIPPED_TOP_LEVEL.has(k)) continue;
    walk(v, [k]);
  }

  const out = new Map<string, VariableConsumerRef[]>();
  for (const [variableKey, byLabel] of acc) out.set(variableKey, [...byLabel.values()]);
  return out;
}

/** 변수 한 건의 총 참조 수 — 배지·삭제 게이트 판정의 단일 숫자. */
export function variableRefTotal(refs: VariableConsumerRef[] | undefined): number {
  return (refs ?? []).reduce((sum, r) => sum + r.count, 0);
}

/**
 * 조망 한 줄 요약 — "조회 파라미터 지정 · 본문 표시 형식 분기(switch) 3곳".
 * 참조가 없으면 빈 문자열(호출부가 "참조 없음" 회색 문구로 그린다).
 *
 * 수량 표기는 `×N`이 아니라 `N곳`이다 — `×N`은 **호출 횟수**로 읽힌다(2026-08-10 사용자 지적).
 * 이 숫자는 저작 자리(셀·조건 등)의 개수이고, 실제 조회는 자리 수와 무관하게 조회당 1회다
 * (서버 resolve가 `resolved[catalogKey]`에 1회 담고 각 자리는 추출만 한다).
 */
export function variableRefSummary(refs: VariableConsumerRef[] | undefined): string {
  return (refs ?? [])
    .map((r) => (r.count > 1 ? `${r.label} ${r.count}곳` : r.label))
    .join(' · ');
}

/**
 * 삭제 확인 모달 본문 — 무엇이 깨지는지 형태별로 나열한다.
 *
 * 총계만 보여주면 "3곳"이 조회 파라미터 3개인지 본문 조건 3개인지 알 수 없어 저작자가 판단할 수
 * 없다(파급의 성격이 다르다). 호출부는 참조가 0이면 이 문구 없이 즉시 삭제한다.
 *
 * ⚠️ **한 줄로 만든다** — 확인 모달(`ConfirmModal`)의 문자열 body는 `whitespace-pre-line` 없는
 * `<p>`에 들어가 줄바꿈이 접힌다. 목록처럼 보이게 하려면 `\n`이 아니라 구분자를 써야 한다.
 */
export function variableDeleteConfirmBody(
  variableKey: string,
  refs: VariableConsumerRef[] | undefined,
): string {
  return (
    `${variableRefTotal(refs)}곳이 '${variableKey}'를 참조 중입니다 — ${variableRefSummary(refs)}. ` +
    '삭제하면 이 참조들은 값을 찾지 못해 조건은 거짓이 되고, 조회 파라미터 지정은 "없는 변수"로 남습니다. ' +
    '참조를 먼저 정리하거나 다른 변수로 재지정하세요.'
  );
}
